/**
 * Algolia client factory for storefront-multibrand.
 *
 * Creates singleton clients for SSR and browser environments.
 * Uses algoliasearch v5 API.
 */

import { algoliasearch, type SearchClient } from 'algoliasearch';
import { ALGOLIA_CONFIG } from './config';

// Singleton clients
let ssrClient: SearchClient | null = null;
let browserClient: SearchClient | null = null;

/**
 * Get the Algolia search client.
 *
 * Creates a singleton per environment (SSR vs browser) to avoid
 * creating multiple clients and connection overhead.
 *
 * @param options.ssr - Force SSR mode (auto-detected if not provided)
 * @returns Algolia SearchClient instance
 */
export function getAlgoliaClient(options: { ssr?: boolean } = {}): SearchClient {
  const { ssr = typeof window === 'undefined' } = options;

  if (!ALGOLIA_CONFIG.appId || !ALGOLIA_CONFIG.apiKey) {
    throw new Error(
      'Algolia credentials not configured. Set NEXT_PUBLIC_ALGOLIA_APP_ID and NEXT_PUBLIC_ALGOLIA_API_KEY environment variables.'
    );
  }

  if (ssr) {
    if (!ssrClient) {
      ssrClient = algoliasearch(ALGOLIA_CONFIG.appId, ALGOLIA_CONFIG.ssrApiKey);
    }
    return ssrClient;
  }

  if (!browserClient) {
    browserClient = algoliasearch(ALGOLIA_CONFIG.appId, ALGOLIA_CONFIG.apiKey);
  }
  return browserClient;
}

/**
 * Sort replica types (excludes 'relevance' which uses base index).
 */
export type SortReplica = 'price_asc' | 'price_desc' | 'newest';

/**
 * Get an index name for the given locale and product type.
 *
 * @param locale - Locale string (e.g., 'us-en')
 * @param sortBy - Optional sort order for replica index
 * @returns Full index name
 */
export function getProductIndexName(locale: string, sortBy?: SortReplica | 'relevance'): string {
  const baseIndex = ALGOLIA_CONFIG.getProductIndex(locale);

  if (!sortBy || sortBy === 'relevance') {
    return baseIndex;
  }

  // Sort indices are replicas with suffix
  return `${baseIndex}.${sortBy}`;
}

/**
 * Reset clients (useful for testing).
 */
export function resetClients(): void {
  ssrClient = null;
  browserClient = null;
}

// Track warmup status to avoid multiple warmups
let isWarmedUp = false;
let warmupPromise: Promise<void> | null = null;

/**
 * Warm up the Algolia connection.
 *
 * Makes a minimal getObjects call to establish the HTTP connection
 * to Algolia servers. This call uses Records operations, NOT Search
 * operations, so it doesn't count against your search quota.
 *
 * Call this during server startup or on first layout render to
 * eliminate cold start latency on the first user search.
 *
 * @param locale - Locale for the index (e.g., 'us-en')
 * @returns Promise that resolves when warmup is complete
 */
export async function warmupAlgoliaConnection(locale: string): Promise<void> {
  // Already warmed up
  if (isWarmedUp) return;

  // Warmup in progress, wait for it
  if (warmupPromise) return warmupPromise;

  warmupPromise = (async () => {
    try {
      const client = getAlgoliaClient({ ssr: true });
      const indexName = ALGOLIA_CONFIG.getProductIndex(locale);

      // Make a minimal getObjects call to establish HTTP connection
      // This uses Records operations, not Search quota
      // Using an empty requests array still establishes the connection
      await client.getObjects({
        requests: [{ indexName, objectID: '_warmup_ping_' }],
      });

      isWarmedUp = true;
    } catch {
      // Ignore errors - warmup is best-effort
      // The actual search will work even if warmup fails
    } finally {
      warmupPromise = null;
    }
  })();

  return warmupPromise;
}
